import React, { useMemo } from 'react'
import BodyText from 'components/Luxkit/Typography/BodyText'
import styled, { css, keyframes } from 'styled-components'
import { bezierCurve } from 'styles/tools/constants'
import arrayShuffle from 'lib/array/arrayShuffle'

const LINE_HEIGHT = 20
const SECONDS_PER_PRODUCT = 4
// 0 < x < 1
const PROPORTION_NOT_TRANSITIONING = 0.8

const Root = styled.div`
  overflow: hidden;
  max-height: ${LINE_HEIGHT}px;
`

const frames = (count: number) => {
  let styles = ''
  const gap = 100 / count * PROPORTION_NOT_TRANSITIONING

  for (let i = 0; i <= count; i++) {
    const start = i / count * 100

    styles += `
      ${start}% {
        transform: translateY(-${(i + 0.5) * LINE_HEIGHT * 2}px);
      }

      ${start + gap}% {
        transform: translateY(-${(i + 0.5) * LINE_HEIGHT * 2}px);
      }
    `
  }

  return css`${styles}`
}

const flip = (count: number) => keyframes`
  ${frames(count)}
`

const Flipper = styled.div<{ $count: number; }>`
  animation: ${props => flip(props.$count)} ${props => props.$count * SECONDS_PER_PRODUCT}s ${bezierCurve} infinite;
`

const ExampleText = styled(BodyText)`
  line-height: ${LINE_HEIGHT}px; // BodyText already sets this, but since it's crucial to the correct functioning of the component, let's set it explicitly
  margin-top: ${LINE_HEIGHT}px;
`

const examples = [
  'ABC Hotel check in 3pm',
  'Visit the National Gallery',
  'Climb the Statue of Liberty on the 15th',
  'Bus from Hyde Park to Stonehenge',
  'Fly from Adelaide to Sydney',
  'Museum of Modern Art',
  'Dinner at La Trattoria 8pm',
]

interface Props {
  className?: string;
}

function ExampleFlipper({ className }: Props) {
  const shuffledExamples = useMemo(() => arrayShuffle(examples), [])

  return (
    <Root className={className}>
      <Flipper $count={shuffledExamples.length}>
        {shuffledExamples.map((example, i) => (
          <ExampleText colour="neutral-four" variant="medium" key={i}>
            e.g. {example}
          </ExampleText>
        ))}
        <ExampleText colour="neutral-four" variant="medium" key="first">
          e.g. {shuffledExamples[0]}
        </ExampleText>
      </Flipper>
    </Root>
  )
}

export default ExampleFlipper
